'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { TextReveal } from './primitives/TextReveal';
import { LuxuryIcon, BellIcon, type IconName } from './icons/LuxuryIcons';

/**
 * PriceDropAlerts — fully operational price drop alert subscription.
 *
 * Left pane: marketing demo — toasts stream in to show what an alert
 * feels like in the inbox.
 *
 * Right pane: real subscription form. Hits POST /api/price-alerts/subscribe
 * with { email, brand, model, currency, targetPrice }. The cron sweep
 * runs every 6h, re-queries Serper for market median, and sends the
 * user an email the moment the piece falls below their target.
 */

interface DemoAlert {
  id: number;
  brand: string;
  model: string;
  icon: IconName;
  oldPrice: number;
  newPrice: number;
  retailer: string;
  time: string;
}

const demoAlerts: DemoAlert[] = [
  { id: 1, brand: 'Hermès',  model: 'Kelly 28',      icon: 'handbag', oldPrice: 72000, newPrice: 64500, retailer: 'The Luxury Closet',    time: '2 min ago' },
  { id: 2, brand: 'Rolex',   model: 'Submariner',    icon: 'watch',   oldPrice: 58000, newPrice: 54200, retailer: 'Vestiaire Collective', time: 'just now' },
  { id: 3, brand: 'Chanel',  model: 'Classic Flap',  icon: 'wallet',  oldPrice: 42000, newPrice: 37800, retailer: 'Fashionphile',          time: '5 min ago' },
];

const API_BASE = 'https://api.mysophia.tech';

type Status = 'idle' | 'submitting' | 'done' | 'error';

export function PriceDropAlerts() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.35 });
  const [visible, setVisible] = useState<number[]>([]);

  // Real form state
  const [email, setEmail] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [target, setTarget] = useState('');
  const [currency, setCurrency] = useState<'AED' | 'SAR' | 'USD' | 'EUR' | 'GBP'>('AED');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!inView) return;
    demoAlerts.forEach((a, i) => {
      setTimeout(() => setVisible((prev) => [...prev, a.id]), 400 + i * 900);
    });
  }, [inView]);

  function dismiss(id: number) {
    setVisible((prev) => prev.filter((v) => v !== id));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;

    const targetNum = Number(target.replace(/[,\s]/g, ''));
    if (!email.trim() || !brand.trim() || !model.trim() || !Number.isFinite(targetNum) || targetNum <= 0) {
      setStatus('error');
      setErrorMsg(isAr ? 'الرجاء ملء جميع الحقول بشكل صحيح.' : 'Please fill every field correctly.');
      return;
    }

    setStatus('submitting');
    setErrorMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/price-alerts/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          brand: brand.trim(),
          model: model.trim(),
          currency,
          targetPrice: targetNum,
          locale,
        }),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!res.ok || !json.success) {
        setStatus('error');
        setErrorMsg(json.error ?? (isAr ? 'تعذّر حفظ التنبيه. حاول لاحقًا.' : 'Could not save the alert. Try again.'));
        return;
      }
      setStatus('done');
    } catch {
      setStatus('error');
      setErrorMsg(isAr ? 'مشكلة في الاتصال.' : 'Network error. Try again.');
    }
  }

  const copy = isAr
    ? {
        ribbon: 'مباشر',
        eyebrow: 'تنبيهات انخفاض السعر',
        heading: 'تُخبرك اللحظة التي تستطيع فيها الشراء.',
        sub: 'حدّد قطعة وسعرًا مستهدفًا. Sophia تراقب 42 متجرًا كل 6 ساعات وترسل لك إيميل اللحظة التي ينخفض فيها متوسط السوق دون حدّك.',
        savedAt: 'عند',
        view: 'عرض',
        dismiss: 'إغلاق',
        formTitle: 'فعّل تنبيهًا',
        formSub: 'نبريك فور انخفاض متوسط السوق دون الحد الذي تحدّده.',
        emailLabel: 'البريد الإلكتروني',
        brandLabel: 'الماركة',
        modelLabel: 'الطراز',
        targetLabel: 'السعر المستهدف',
        currencyLabel: 'العملة',
        emailPh: 'your@email.com',
        brandPh: 'مثال: هيرميس',
        modelPh: 'مثال: بيركن ٢٥',
        targetPh: '80000',
        submit: 'فعّل التنبيه',
        submitting: 'يُحفظ…',
        done: 'تم حفظ التنبيه. سنرسل لك إيميلًا عند انخفاض السعر.',
        addAnother: 'إضافة تنبيه آخر',
      }
    : {
        ribbon: 'Live',
        eyebrow: 'Price drop alerts',
        heading: "We'll tell you the moment you can buy.",
        sub: 'Pick a piece and a target price. Sophia checks 42 retailers every 6 hours and emails you the instant the market median drops below your threshold.',
        savedAt: 'at',
        view: 'View',
        dismiss: 'Dismiss',
        formTitle: 'Create an alert',
        formSub: "We'll email you the instant the market median dips below your target.",
        emailLabel: 'Email',
        brandLabel: 'Brand',
        modelLabel: 'Model',
        targetLabel: 'Target price',
        currencyLabel: 'Currency',
        emailPh: 'your@email.com',
        brandPh: 'e.g. Hermès',
        modelPh: 'e.g. Birkin 25',
        targetPh: '80000',
        submit: 'Activate alert',
        submitting: 'Saving…',
        done: "Alert saved. We'll email you when the price drops.",
        addAnother: 'Add another alert',
      };

  return (
    <section
      ref={sectionRef}
      className="relative py-12 sm:py-16 md:py-32 border-t border-white/5 bg-gradient-to-b from-obsidian-800 to-obsidian"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold">
              {copy.eyebrow}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-emerald/15 text-emerald-soft text-[9px] tracking-[0.25em] uppercase font-bold border border-emerald/20">
              {copy.ribbon}
            </span>
          </div>
          <TextReveal as="h2" className="font-serif text-display-sm text-cream leading-[0.98]">
            {copy.heading}
          </TextReveal>
          <p className="mt-6 text-fog-300 text-lg leading-relaxed max-w-xl">{copy.sub}</p>
        </div>

        <div className="grid lg:grid-cols-[1.1fr,1fr] gap-8 lg:gap-10 items-start">
          {/* Toast stack demo (unchanged) */}
          <div className="relative min-h-[360px] md:min-h-[460px] rounded-luxe bg-obsidian-700/40 border border-white/5 p-5 md:p-8 overflow-hidden">
            <div className="absolute top-6 start-6 text-[10px] tracking-[0.3em] uppercase text-fog-500 font-semibold">
              LIVE · DEMO
            </div>
            <div className="relative mt-14 space-y-3" dir="ltr">
              <AnimatePresence>
                {demoAlerts
                  .filter((a) => visible.includes(a.id))
                  .map((a, stackIdx) => {
                    const pct = Math.round(((a.oldPrice - a.newPrice) / a.oldPrice) * 100);
                    return (
                      <motion.div
                        key={a.id}
                        layout
                        initial={{ opacity: 0, x: 80, rotate: 3, scale: 0.95 }}
                        animate={{ opacity: 1, x: 0, rotate: stackIdx * -0.4, scale: 1 }}
                        exit={{ opacity: 0, x: 120, transition: { duration: 0.3 } }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="rounded-luxe bg-cream text-obsidian-900 p-4 shadow-card-lg border border-obsidian-900/5 max-w-md"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose/20 to-rose/5 grid place-items-center shrink-0 text-obsidian-900">
                            <LuxuryIcon kind={a.icon} size={24} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-[10px] tracking-widest uppercase text-rose font-bold">↓ {pct}% drop</span>
                              <span className="text-[10px] text-fog-500">· {a.time}</span>
                            </div>
                            <div className="text-sm font-medium leading-snug">
                              <span className="font-semibold">{a.brand}</span> {a.model}
                            </div>
                            <div className="text-xs text-fog-700 mt-0.5">
                              <span className="line-through">AED {a.oldPrice.toLocaleString()}</span>{' '}
                              → <span className="font-mono font-bold text-obsidian-900">AED {a.newPrice.toLocaleString()}</span>{' '}
                              {copy.savedAt} {a.retailer}
                            </div>
                          </div>
                          <button
                            onClick={() => dismiss(a.id)}
                            className="w-6 h-6 rounded-full grid place-items-center text-fog-500 hover:bg-obsidian-900/5 transition"
                            aria-label={copy.dismiss}
                          >
                            ✕
                          </button>
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button className="flex-1 px-3 py-2 rounded-pill bg-obsidian-900 text-cream text-xs font-semibold hover:bg-obsidian-700 transition">
                            {copy.view}
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
              </AnimatePresence>
            </div>
          </div>

          {/* Real subscribe form */}
          <div className="sticky top-24">
            <div className="rounded-luxe bg-gradient-to-br from-obsidian-700 to-obsidian-800 border border-gold/15 p-6 md:p-8">
              <div className="w-10 h-10 rounded-luxe bg-gold/10 border border-gold/20 grid place-items-center mb-4 text-gold-soft">
                <BellIcon size={22} />
              </div>
              <div className="font-serif text-2xl text-cream mb-2 leading-snug">{copy.formTitle}</div>
              <p className="text-sm text-fog-300 mb-6 leading-relaxed">{copy.formSub}</p>

              <AnimatePresence mode="wait">
                {status === 'done' ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="rounded-xl bg-emerald/10 border border-emerald/20 p-4 text-start"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-emerald text-xl">✓</span>
                      <div>
                        <div className="text-sm text-emerald-soft font-semibold mb-1">{copy.done}</div>
                        <div className="text-xs text-fog-400 font-mono mt-1">
                          {brand} {model} ≤ {currency} {Number(target.replace(/[,\s]/g, '')).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setStatus('idle');
                        setEmail('');
                        setBrand('');
                        setModel('');
                        setTarget('');
                      }}
                      className="mt-4 text-xs tracking-[0.2em] uppercase text-gold-soft hover:text-gold font-bold transition"
                    >
                      + {copy.addAnother}
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={onSubmit}
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                    dir={isAr ? 'rtl' : 'ltr'}
                  >
                    <Field label={copy.emailLabel}>
                      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={copy.emailPh} className={inputCls} dir="ltr" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label={copy.brandLabel}>
                        <input type="text" required value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={copy.brandPh} className={inputCls} />
                      </Field>
                      <Field label={copy.modelLabel}>
                        <input type="text" required value={model} onChange={(e) => setModel(e.target.value)} placeholder={copy.modelPh} className={inputCls} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-[1fr,auto] gap-3">
                      <Field label={copy.targetLabel}>
                        <input type="text" inputMode="numeric" required value={target} onChange={(e) => setTarget(e.target.value)} placeholder={copy.targetPh} className={inputCls} dir="ltr" />
                      </Field>
                      <Field label={copy.currencyLabel}>
                        <select value={currency} onChange={(e) => setCurrency(e.target.value as typeof currency)} className={inputCls}>
                          <option value="AED">AED</option>
                          <option value="SAR">SAR</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </Field>
                    </div>
                    {errorMsg && (
                      <div className="text-xs text-rose">{errorMsg}</div>
                    )}
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full mt-2 px-5 py-3 rounded-pill bg-gold-gradient text-obsidian-900 text-sm font-bold hover:shadow-glow-gold transition disabled:opacity-60"
                    >
                      {status === 'submitting' ? copy.submitting : copy.submit}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  'w-full px-4 py-2.5 rounded-lg bg-obsidian-900 border border-white/10 text-cream text-sm placeholder:text-fog-500 focus:border-gold/40 focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase text-fog-500 font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}
